// @ts-nocheck -- legacy typings pending cleanup; runtime behavior unchanged
/**
 * ClinicalOS Backup v1 — registro de tabelas, dependências e chaves naturais.
 * Aditivo: nenhuma alteração de schema é necessária.
 */

export const BACKUP_FORMAT = "clinicalos-backup" as const;
export const BACKUP_FORMAT_VERSION = 1 as const;

export type BackupGroup = "core" | "pharm_private" | "derived" | "audit";

export interface TableSpec {
  /** nome da tabela no banco */
  table: string;
  group: BackupGroup;
  /** coluna de chave primária (algumas tabelas usam owner_id) */
  pk: "id" | "owner_id";
  /** possui coluna patient_id (usado no escopo por paciente) */
  patientScoped: boolean;
  /** coluna FK -> nome lógico da entidade referenciada (para remapeamento) */
  refs?: Record<string, string>;
  /** colunas usadas para montar chave natural de dedupe */
  keyColumns?: string[];
  /** chave natural para dedupe idempotente; null = usar apenas id */
  naturalKey?: (row: Record<string, any>) => string | null;
  /** nunca reaplicado durante restore */
  restoreDisabled?: boolean;
  label: string;
}

const n = (v: unknown) => (v === null || v === undefined ? "" : String(v).trim().toLowerCase());
const t = (v: unknown) => {
  if (!v) return "";
  const d = new Date(v as string);
  return Number.isNaN(d.getTime()) ? String(v) : d.toISOString();
};

export const BACKUP_TABLE_SPECS: TableSpec[] = [
  {
    table: "substances",
    label: "Substâncias privadas",
    group: "pharm_private",
    pk: "id",
    patientScoped: false,
    keyColumns: ["id", "name", "generic_name", "is_global"],
    naturalKey: (r) => `${n(r.name || r.generic_name)}|${n(r.is_global ? "global" : "private")}`,
  },
  {
    table: "substance_formulations",
    label: "Formulações privadas",
    group: "pharm_private",
    pk: "id",
    patientScoped: false,
    refs: { substance_id: "substances" },
    keyColumns: ["id", "substance_id", "formulation_name", "route"],
    naturalKey: (r) => `${n(r.substance_id)}|${n(r.formulation_name)}|${n(r.route)}`,
  },
  {
    table: "app_settings",
    label: "Preferências do aplicativo",
    group: "core",
    pk: "owner_id",
    patientScoped: false,
  },
  {
    table: "patients",
    label: "Pacientes",
    group: "core",
    pk: "id",
    patientScoped: false,
    keyColumns: ["id", "full_name", "birth_date"],
    naturalKey: (r) => `${n(r.full_name)}|${n(r.birth_date)}`,
  },
  {
    table: "substance_material_profiles",
    label: "Perfis de material/amostra",
    group: "pharm_private",
    pk: "id",
    patientScoped: false,
    refs: { patient_id: "patients", substance_id: "substances" },
    keyColumns: ["id", "owner_id", "canonical_substance_name", "profile_name", "sample_label"],
    naturalKey: (r) => `${n(r.owner_id)}|${n(r.canonical_substance_name)}|${n(r.profile_name)}|${n(r.sample_label)}`,
  },
  {
    table: "patient_medications",
    label: "Tratamento / medicações",
    group: "core",
    pk: "id",
    patientScoped: true,
    refs: { patient_id: "patients", substance_id: "substances", formulation_id: "substance_formulations" },
    keyColumns: ["id", "patient_id", "substance_id", "free_text_name", "start_date", "current_dose"],
    naturalKey: (r) =>
      `${n(r.patient_id)}|${n(r.substance_id || r.free_text_name)}|${n(r.start_date)}|${n(r.current_dose)}`,
  },
  {
    table: "clinical_sessions",
    label: "Sessões clínicas",
    group: "core",
    pk: "id",
    patientScoped: true,
    refs: { patient_id: "patients" },
    keyColumns: ["id", "patient_id", "session_at", "name", "session_type"],
    naturalKey: (r) => `${n(r.patient_id)}|${t(r.session_at)}|${n(r.name || r.session_type)}`,
  },
  {
    table: "patient_medication_regimen_events",
    label: "Linha longitudinal de tratamentos",
    patientScoped: true,
    refs: { patient_id: "patients", patient_medication_id: "patient_medications", session_id: "clinical_sessions" },
    keyColumns: ["id", "patient_id", "patient_medication_id", "event_at", "event_type"],
  },
  {
    table: "medication_dose_logs",
    label: "Registros de dose",
    group: "core",
    pk: "id",
    patientScoped: true,
    refs: {
      patient_id: "patients",
      session_id: "clinical_sessions",
      substance_id: "substances",
      formulation_id: "substance_formulations",
      patient_medication_id: "patient_medications",
      material_profile_id: "substance_material_profiles",
    },
    keyColumns: ["id", "patient_id", "substance_id", "substance_name", "actual_time", "dose_amount", "dose_unit", "route"],
    naturalKey: (r) => doseNaturalKey(r),
  },
  {
    table: "session_checkins",
    label: "Check-ins",
    group: "core",
    pk: "id",
    patientScoped: true,
    refs: { patient_id: "patients", session_id: "clinical_sessions" },
    keyColumns: ["id", "patient_id", "checkin_at"],
    naturalKey: (r) => `${n(r.patient_id)}|${t(r.checkin_at)}`,
  },
  {
    table: "patient_target_symptoms",
    label: "Sintomas-alvo",
    group: "core",
    pk: "id",
    patientScoped: true,
    refs: { patient_id: "patients" },
    keyColumns: ["id", "patient_id", "symptom_name"],
    naturalKey: (r) => `${n(r.patient_id)}|${n(r.symptom_name)}`,
  },
  {
    table: "symptom_measurements",
    label: "Medidas de sintomas",
    group: "core",
    pk: "id",
    patientScoped: true,
    refs: { patient_id: "patients", session_id: "clinical_sessions", target_symptom_id: "patient_target_symptoms" },
    keyColumns: ["id", "patient_id", "symptom_name", "measured_at"],
    naturalKey: (r) => `${n(r.patient_id)}|${n(r.symptom_name)}|${t(r.measured_at)}`,
  },
  {
    table: "vital_signs",
    label: "Sinais vitais clínicos",
    group: "core",
    pk: "id",
    patientScoped: true,
    refs: { patient_id: "patients", session_id: "clinical_sessions", linked_dose_id: "medication_dose_logs" },
    keyColumns: ["id", "patient_id", "measured_at", "systolic_mm_hg", "diastolic_mm_hg", "heart_rate_bpm"],
    naturalKey: (r) =>
      `${n(r.patient_id)}|${t(r.measured_at)}|${n(r.systolic_mm_hg)}/${n(r.diastolic_mm_hg)}|${n(r.heart_rate_bpm)}`,
  },
  {
    table: "patient_vital_signs",
    label: "Sinais vitais",
    group: "core",
    pk: "id",
    patientScoped: true,
    refs: { patient_id: "patients", linked_dose_id: "medication_dose_logs" },
    keyColumns: ["id", "patient_id", "measured_at", "systolic_bp", "diastolic_bp", "heart_rate"],
    naturalKey: (r) =>
      `${n(r.patient_id)}|${t(r.measured_at)}|${n(r.systolic_bp)}/${n(r.diastolic_bp)}|${n(r.heart_rate)}`,
  },
  {
    table: "meal_events",
    label: "Refeições",
    group: "core",
    pk: "id",
    patientScoped: true,
    refs: { patient_id: "patients" },
    keyColumns: ["id", "patient_id", "occurred_at", "meal_type"],
    naturalKey: (r) => `${n(r.patient_id)}|${t(r.occurred_at)}|${n(r.meal_type)}`,
  },
  {
    table: "sleep_periods",
    label: "Períodos de sono",
    group: "core",
    pk: "id",
    patientScoped: true,
    refs: { patient_id: "patients" },
    keyColumns: ["id", "patient_id", "sleep_start_at"],
    naturalKey: (r) => `${n(r.patient_id)}|${t(r.sleep_start_at)}`,
  },
  {
    table: "substance_use_logs",
    label: "Substâncias não prescritas",
    group: "core",
    pk: "id",
    patientScoped: true,
    refs: { patient_id: "patients", session_id: "clinical_sessions", substance_id: "substances" },
    keyColumns: ["id", "patient_id", "substance_name", "used_at", "amount", "unit"],
    naturalKey: (r) => `${n(r.patient_id)}|${n(r.substance_name)}|${t(r.used_at)}|${n(r.amount)}|${n(r.unit)}`,
  },
  {
    table: "medication_inventory",
    label: "Estoque",
    group: "core",
    pk: "id",
    patientScoped: true,
    refs: {
      patient_id: "patients",
      substance_id: "substances",
      formulation_id: "substance_formulations",
      patient_medication_id: "patient_medications",
    },
    keyColumns: ["id", "patient_id", "medication_name", "lot_number", "unit"],
    naturalKey: (r) => `${n(r.patient_id)}|${n(r.medication_name)}|${n(r.lot_number)}|${n(r.unit)}`,
  },
  {
    table: "medication_inventory_movements",
    label: "Movimentos de estoque",
    group: "core",
    pk: "id",
    patientScoped: true,
    refs: { patient_id: "patients", inventory_id: "medication_inventory" },
    keyColumns: ["id", "inventory_id", "created_at", "movement_type", "amount"],
    naturalKey: (r) => `${n(r.inventory_id)}|${t(r.created_at)}|${n(r.movement_type)}|${n(r.amount)}`,
  },
  {
    table: "patient_psylog_events",
    label: "Timeline clínica",
    group: "core",
    pk: "id",
    patientScoped: true,
    refs: {
      patient_id: "patients",
      session_id: "clinical_sessions",
      linked_session_id: "clinical_sessions",
      linked_dose_id: "medication_dose_logs",
      linked_medication_id: "patient_medications",
    },
    keyColumns: ["id", "patient_id", "dedupe_key", "event_at", "event_type", "title"],
    naturalKey: (r) =>
      r.dedupe_key ? `dedupe:${n(r.dedupe_key)}` : `${n(r.patient_id)}|${t(r.event_at)}|${n(r.event_type)}|${n(r.title)}`,
  },
  {
    table: "patient_substance_response_profiles",
    label: "Perfis de resposta individual",
    group: "core",
    pk: "id",
    patientScoped: true,
    refs: { patient_id: "patients", substance_id: "substances" },
    keyColumns: ["id", "patient_id", "substance_id", "substance_name"],
    naturalKey: (r) => `${n(r.patient_id)}|${n(r.substance_id || r.substance_name)}`,
  },
  {
    table: "diagnostic_hypotheses",
    label: "Hipóteses diagnósticas",
    group: "core",
    pk: "id",
    patientScoped: true,
    refs: { patient_id: "patients", session_id: "clinical_sessions" },
    keyColumns: ["id", "patient_id", "diagnostic_label", "created_at"],
    naturalKey: (r) => `${n(r.patient_id)}|${n(r.diagnostic_label)}|${t(r.created_at)}`,
  },
  {
    table: "diagnostic_hypothesis_criteria",
    label: "Critérios diagnósticos",
    group: "core",
    pk: "id",
    patientScoped: false,
    refs: { hypothesis_id: "diagnostic_hypotheses" },
    keyColumns: ["id", "hypothesis_id", "criterion_code", "criterion_text"],
    naturalKey: (r) => `${n(r.hypothesis_id)}|${n(r.criterion_code || r.criterion_text)}`,
  },
  {
    table: "diagnostic_hypothesis_symptom_links",
    label: "Vínculos hipótese/sintoma",
    group: "core",
    pk: "id",
    patientScoped: false,
    refs: { hypothesis_id: "diagnostic_hypotheses", target_symptom_id: "patient_target_symptoms" },
    keyColumns: ["id", "hypothesis_id", "target_symptom_id"],
    naturalKey: (r) => `${n(r.hypothesis_id)}|${n(r.target_symptom_id)}`,
  },
  {
    table: "diagnostic_hypothesis_revisions",
    label: "Revisões de hipótese",
    group: "core",
    pk: "id",
    patientScoped: false,
    refs: { hypothesis_id: "diagnostic_hypotheses" },
    keyColumns: ["id", "hypothesis_id", "created_at"],
    naturalKey: (r) => `${n(r.hypothesis_id)}|${t(r.created_at)}`,
  },
  {
    table: "clinical_interactions",
    label: "Interações revisadas",
    group: "core",
    pk: "id",
    patientScoped: true,
    refs: { patient_id: "patients", session_id: "clinical_sessions" },
    keyColumns: ["id", "patient_id", "summary", "created_at"],
    naturalKey: (r) => `${n(r.patient_id)}|${n(r.summary)}|${t(r.created_at)}`,
  },
  {
    table: "pharmacology_interaction_assessments",
    label: "Avaliações de interação",
    group: "core",
    pk: "id",
    patientScoped: true,
    refs: { patient_id: "patients", session_id: "clinical_sessions" },
    keyColumns: ["id", "patient_id", "created_at", "category"],
    naturalKey: (r) => `${n(r.patient_id)}|${t(r.created_at)}|${n(r.category)}`,
  },
  {
    table: "substance_clinical_annotations",
    label: "Anotações farmacológicas",
    group: "pharm_private",
    pk: "id",
    patientScoped: true,
    refs: { substance_id: "substances", formulation_id: "substance_formulations", patient_id: "patients" },
    keyColumns: ["id", "substance_id", "raw_note", "created_at"],
    naturalKey: (r) => `${n(r.substance_id)}|${n(r.raw_note).slice(0, 120)}`,
  },
  {
    table: "substance_effect_profiles",
    label: "Perfis PD (0-100)",
    group: "pharm_private",
    pk: "id",
    patientScoped: false,
    refs: { substance_id: "substances", formulation_id: "substance_formulations" },
    keyColumns: ["id", "substance_id", "formulation_id", "effect_key"],
    naturalKey: (r) => `${n(r.substance_id)}|${n(r.formulation_id)}|${n(r.effect_key)}`,
  },
  {
    table: "clinical_knowledge_sources",
    label: "Fontes de conhecimento",
    group: "pharm_private",
    pk: "id",
    patientScoped: false,
    keyColumns: ["id", "source_name", "url"],
    naturalKey: (r) => `${n(r.source_name)}|${n(r.url)}`,
  },
  {
    table: "clinical_effect_forecasts",
    label: "Projeções de efeito",
    group: "derived",
    pk: "id",
    patientScoped: true,
    refs: { patient_id: "patients", session_id: "clinical_sessions" },
    keyColumns: ["id", "patient_id", "generated_at"],
    naturalKey: (r) => `${n(r.patient_id)}|${t(r.generated_at)}`,
  },
  {
    table: "clinical_reports",
    label: "Relatórios",
    group: "derived",
    pk: "id",
    patientScoped: true,
    refs: { patient_id: "patients" },
    keyColumns: ["id", "patient_id", "title", "created_at"],
    naturalKey: (r) => `${n(r.patient_id)}|${n(r.title)}|${t(r.created_at)}`,
  },
  {
    table: "clinical_ai_analyses",
    label: "Análises assistidas",
    group: "derived",
    pk: "id",
    patientScoped: true,
    refs: { patient_id: "patients", session_id: "clinical_sessions" },
    keyColumns: ["id", "patient_id", "created_at", "source"],
    naturalKey: (r) => `${n(r.patient_id)}|${t(r.created_at)}|${n(r.source)}`,
  },
  {
    table: "curve_results",
    label: "Resultados de curvas",
    group: "derived",
    pk: "id",
    patientScoped: true,
    refs: { patient_id: "patients", session_id: "clinical_sessions" },
    keyColumns: ["id", "patient_id", "generated_at"],
    naturalKey: (r) => `${n(r.patient_id)}|${t(r.generated_at)}`,
  },
  {
    table: "axis_audits",
    label: "Auditorias de eixos",
    group: "derived",
    pk: "id",
    patientScoped: true,
    refs: { patient_id: "patients", session_id: "clinical_sessions" },
    keyColumns: ["id", "patient_id", "generated_at"],
    naturalKey: (r) => `${n(r.patient_id)}|${t(r.generated_at)}`,
  },
  {
    table: "integration_settings",
    label: "Integrações (metadados)",
    group: "derived",
    pk: "id",
    patientScoped: false,
    keyColumns: ["id", "provider"],
    naturalKey: (r) => n(r.provider),
    restoreDisabled: true,
  },
  {
    table: "audit_logs",
    label: "Histórico de auditoria",
    group: "audit",
    pk: "id",
    patientScoped: false,
    keyColumns: ["id"],
    restoreDisabled: true,
  },
];

/** chave natural canônica de uma dose */
export function doseNaturalKey(r: Record<string, any>): string {
  return [
    n(r.patient_id),
    n(r.substance_id || r.substance_name),
    t(r.actual_time),
    n(r.dose_amount),
    n(r.dose_unit),
    n(r.route),
  ].join("|");
}

export function specFor(table: string): TableSpec | undefined {
  return BACKUP_TABLE_SPECS.find((s) => s.table === table);
}

/** ordem de restauração: entidades pai antes de filhas (ordem do registro) */
export const RESTORE_ORDER: string[] = BACKUP_TABLE_SPECS.filter((s) => !s.restoreDisabled).map((s) => s.table);
